import { useTranslation } from "react-i18next";
import i18n from "i18next";
const t = (key: string, fallback?: string) => i18n.t(key, fallback || key);
import {
	AppstoreOutlined,
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	HistoryOutlined,
	MoreOutlined,
	ShareAltOutlined,
	SwapOutlined,
	TableOutlined,
} from "@ant-design/icons";
import {
	Avatar,
	Button,
	Checkbox,
	Dropdown,
	Flex,
	Form,
	MenuProps,
	message,
	Modal,
	Space,
	TableColumnsType,
	Tabs,
	TabsProps,
	Tooltip
} from "antd";
import dayjs from "dayjs";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import CardData from "../../../../../components/card-data/CardData";
import TableData from "../../../../../components/table-data/TableData";
import { routesConfig } from "../../../../../config/routes";
import useKeyword from "../../../../../hooks/useKeyword";
import {
	deleteLearningMaterial,
	deleteMultipleLearningMaterials,
	getLastEditedBy,
	getOwnerList,
	getSchoolOwnsList,
	moveItemLearningMaterial,
} from "../../../../../service/learning-material";
import { fetchLearningMaterials } from "../../../../../service/learning-repository";
import { LearningMaterialStore, useLearningRepositoryStore } from "../../../../../stores/stores";
import {
	LearningMaterialV2Type
} from "../../../../../types/learning-material";
import { getFirstLetter, RenderUnitIcon } from "../../../../../utils/functionUltils";
import { notifyError } from "../../../../../utils/notification";
import FormFilter from "../FormFilter/FormFilter";
import ResourceCard from "../resource-card/ResourceCard";
import "./LearningMaterialView.scss";
import ChooseLearningMaterialModal from "../../../../../components/modal/choose-learning-material/ChooseLearningMaterialModal";

export interface LearningMaterialViewProps {
	folderId?: string | null;
	showFilter: boolean;
	onCloseFilter: () => void;
	onEdit?: (rec: LearningMaterialV2Type) => void;
	onShowHistory?: (shareId: number) => void;
	onSharePermission?: (rec: LearningMaterialV2Type) => void;
	disableActions?: boolean;
	selectOne?: boolean;
	onSelectRow?: (rows: LearningMaterialV2Type[]) => void;
	moduleFilter?: string;
	selectedRow?: LearningMaterialV2Type[];
	tabBarExtraContent?: any;
	modalMode?: "select" | "saveTemplate" | "moveItem";
	enableMultiDelete?: boolean;
	disableTabChange?: boolean;
	movedId?: number | null;
	movingItems?: LearningMaterialV2Type[];
}

const LearningMaterialView: React.FC<LearningMaterialViewProps> = ({
	folderId,
	showFilter,
	onCloseFilter,
	onEdit,
	onShowHistory,
	onSharePermission,
	disableActions,
	selectOne,
	onSelectRow,
	moduleFilter,
	selectedRow,
	tabBarExtraContent,
	modalMode,
	enableMultiDelete,
	disableTabChange,
	movedId: propMovedId,
	movingItems
}) => {
  const { t } = useTranslation();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { reload } = LearningMaterialStore();
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState<any[]>([]);
	const [viewMode, setViewMode] = useState<'table' | 'card'>(
		localStorage.getItem('learning-material-view') === 'card' ? 'card' : 'table'
	);
	const [total, setTotal] = useState(0);
	const [selectedItems, setSelectedItems] = useState<LearningMaterialV2Type[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isMoveModalOpen, setIsMoveModalOpen] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [movedId, setMovedId] = useState<number | null>(null);
	const allowMultiDelete = enableMultiDelete && !modalMode && !selectOne && !disableActions;
	const searchParams = new URLSearchParams(location.search);

	const isMineFolder = useKeyword("isMine");
	const isSharedFolder = useKeyword("isShared");

	const calculateTableScroll = useCallback(() => {
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;

		const baseOffset = 150;
		const widthRatio = windowWidth / 1920;
		const dynamicOffset = Math.min(150, Math.max(50, widthRatio * 150));
		const filterOffset = showFilter ? 120 : 0;

		const finalOffset = baseOffset + dynamicOffset + filterOffset + 48;
		const yScroll = Math.max(200, windowHeight - finalOffset);

		const minXScroll = 800;
		const maxXScroll = 1500;
		const baseXScroll = 1200;

		const xScrollRatio = Math.min(1, windowWidth / 1400);
		const dynamicXScroll = baseXScroll + (maxXScroll - baseXScroll) * xScrollRatio;
		const xScroll = Math.min(maxXScroll, Math.max(minXScroll, dynamicXScroll));

		return { x: Math.round(xScroll), y: Math.round(yScroll) };
	}, [modalMode, showFilter]);

	const [tableScroll, setTableScroll] = useState(calculateTableScroll);

	const {
		keyword,
		page,
		size,
		tab,
		ownerId,
		updaterId,
		enterpriseUuid,
		updatedDate,
		set: setFilter,
		reset: resetFilter,
	} = useLearningRepositoryStore((s) => ({
		keyword: s.keyword,
		page: s.page,
		size: s.size,
		tab: s.tab as "1" | "2",
		ownerId: (s as any).ownerId,
		updaterId: (s as any).updaterId,
		enterpriseUuid: (s as any).enterpriseUuid,
		updatedDate: (s as any).updatedDate,
		set: s.set,
		reset: s.reset,
	}));
	const { set } = useLearningRepositoryStore((s) => ({ set: s.set }));

	const [dataOptions, setDataOptions] = useState({
		ownerList: [] as { value: number; label: string }[],
		schoolOwnsList: [] as { value: string; label: string }[],
		lastEditedBy: [] as { value: number; label: string }[],
	});

	const fetchList = async () => {
		setLoading(true);
		const payload = {
			page,
			size,
			ownerId: tab === "2" ? ownerId : undefined,
			updaterId: tab === "2" ? updaterId : undefined,
			enterpriseUuid: tab === "2" ? enterpriseUuid : undefined,
			updatedDateFrom: updatedDate?.[0] || undefined,
			updatedDateTo: updatedDate?.[1] || undefined,
			keyword: keyword || undefined,
			shared: tab === "2",
			sort: ["created_date,desc"],
			id: folderId,
			unitModule: moduleFilter,
		} as any;

		try {
			const { data } = (await fetchLearningMaterials(payload)).data;
			setRows(data?.content ?? []);
			setTotal(data?.totalElements ?? 0);
			set("folderInfo", data?.parentFolder ?? null);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	}

	const onChangePage = (p: number) => setFilter("page", p);
	const onChangeSize = (s: number) => setFilter("size", s);

	const onDelete = async (mid: number) => {
		try {
			const payload = {
				ids: [mid]
			}
			await deleteMultipleLearningMaterials(payload);
			message.success(t("Xoá thành công", "Xoá thành công"));
			fetchList();
		} catch {
			notifyError(t("Xoá thất bại", "Xoá thất bại"));
		}
	};

	const onDeleteMultiple = async () => {
		if (selectedItems.length === 0) return;

		Modal.confirm({
			title: `${t("Xoá", "Xoá")} ${selectedItems.length} ${t("mục đã chọn", "mục đã chọn")}`,
			content: `${t("Bạn có chắc chắn muốn xoá", "Bạn có chắc chắn muốn xoá")} ${selectedItems.length} ${t("thư mục/học liệu đã chọn? Các mục đã xoá sẽ không thể khôi phục.", "thư mục/học liệu đã chọn? Các mục đã xoá sẽ không thể khôi phục.")}`,
			centered: true,
			okText: t("Đồng ý", "Đồng ý"),
			cancelText: t("Huỷ", "Huỷ"),
			onOk: async () => {
				setIsDeleting(true);
				try {
					if (typeof deleteMultipleLearningMaterials === 'function') {
						const payload = {
							ids: selectedItems.map(item => item.id)
						}
						await deleteMultipleLearningMaterials(payload);
					} else {
						for (const item of selectedItems) {
							await deleteLearningMaterial(item.id);
						}
					}

					message.success(`${t("Đã xoá", "Đã xoá")} ${selectedItems.length} ${t("mục thành công", "mục thành công")}`);
					setSelectedItems([]);
					fetchList();
				} catch (error) {
					notifyError(t("Có lỗi xảy ra khi xoá", "Có lỗi xảy ra khi xoá"));
				} finally {
					setIsDeleting(false);
				}
			},
		});
	};

	const handleSelectRows = (rows: any[]) => {
		if (selectOne) {
			onSelectRow?.(rows);
		} else if (allowMultiDelete) {
			setSelectedItems(rows);
		}
	};

	const onNavigate = (rec: any) => {
		const isUnit = rec?.isUnit;

		const isBeingMoved = modalMode === 'moveItem' && (
			(propMovedId && rec.id === propMovedId) ||
			(movingItems && movingItems.some(item => item.id === rec.id)) ||
			(movedId && rec.id === movedId) ||
			selectedItems.some(item => item.id === rec.id)
		);

		if (isBeingMoved) {
			return;
		}

		if (!isUnit) {
			if (rec?.isMine) {
				searchParams.set("isMine", "true");
				searchParams.delete("isShared");
				set("tab", "1");
			} else {
				searchParams.set("isShared", "true");
				set("tab", "2");
				searchParams.delete("isMine");
			}
		}

		if ((selectOne || modalMode === 'saveTemplate' || modalMode === 'moveItem') && !isUnit) {
			searchParams.set("folderId", rec.id);
			navigate({
				pathname: location.pathname,
				search: searchParams.toString(),
			});
			return;
		}

		if ((selectOne || modalMode === 'saveTemplate' || modalMode === 'moveItem') && isUnit) {
			return;
		}

		const basePath = isUnit
			? `/course-library${routesConfig.learningMaterials}/detail/${rec?.units?.[0]?.id}`
			: `/${routesConfig.courseLibrary}/${routesConfig.learningRepository}/${rec.id}${searchParams.toString() ? "?" + searchParams.toString() : ""}`;

		navigate(basePath);
	};

	const baseColumns: TableColumnsType<any> = useMemo(
		() => [
			{
				title: "STT",
				dataIndex: "numericalOrder",
				width: 60,
			},
			{
				title: t("Tên", "Tên"),
				key: "resource",
				render: (_: any, rec) => {
					const isBeingMoved = modalMode === 'moveItem' && (
						(propMovedId && rec.id === propMovedId) ||
						(movingItems && movingItems.some(item => item.id === rec.id)) ||
						(movedId && rec.id === movedId) ||
						selectedItems.some(item => item.id === rec.id)
					);

					const isDisabled = (modalMode === "saveTemplate" || modalMode === "moveItem") && rec?.isUnit ||
						(modalMode === 'saveTemplate' || modalMode === 'moveItem') && !rec.isCanEdit ||
						isBeingMoved
					// || modalMode === "moveItem" && rec.isChangedAuthor;

					return (
						<div
							className={`flex align-center gap-16 ${isDisabled ? 'disabled-click' : 'cursor-pointer'}`}
							style={{ wordBreak: "break-all" }}
							onClick={() => onNavigate(rec)}
						>
							<div className="learning-icon">
								{rec.isUnit ? (
									<div>
										{RenderUnitIcon(rec.unitModule)}
									</div>
								) : (
									rec.isMine ?
										<img
											src="/assets/icons/folder-icon.svg"
											style={{ minWidth: 32, minHeight: 32 }}
										/> : <img
											src="/assets/icons/folder-shared.svg"
											style={{ minWidth: 32, minHeight: 32 }}
										/>
								)}

								{rec.isChangedAuthor && <div className="learning-icon-child">
									<img
										src="/assets/icons/transfer-icon.svg"
									/>
								</div>}
							</div>
							<Tooltip placement="top" title={rec.name}>
								<div className="learning-material-name">
									{rec.name}
									{isBeingMoved && <span style={{ color: '#999', marginLeft: '8px' }}>({t('Đang di chuyển', 'Đang di chuyển')})</span>}
								</div>
							</Tooltip>
						</div>
					);
				},
			},
			{
				title: t("Chủ sở hữu", "Chủ sở hữu"),
				key: "authors",
				width: 320,
				render: (_: any, rec) => {
					const owner = rec?.authors?.find((it: any) => it.permission === "1")
					return (
						<Flex gap={16} align="center" className="text-secondary">
							<Avatar src={rec.isMine ? owner.avatar : owner.universityMemberUrl} className="table-view-avatar">
								{getFirstLetter(rec.createdByName)}
							</Avatar>
							{rec.isMine ? <div>{t("tôi", "tôi")}</div> : <div>
								<div className="font-weight-5">{owner.universityMember}</div>
								<div>{owner.name}</div>
							</div>}
						</Flex>
					)
				}
			},
			{
				title: t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối"),
				key: "updateTime",
				width: 320,
				render: (_: any, rec) => (
					<div className="text-secondary">
						{rec.isMine ? <div>
							{rec.updatedDate
								? dayjs(rec.updatedDate).format("HH:mm - DD/MM/YYYY")
								: null}
						</div> :
							<Flex gap={16} align="center">
								<Avatar src={rec.enterpriseUpdateUrl} className="table-view-avatar">
									{getFirstLetter(rec.enterpriseUpdateName)}
								</Avatar>
								<div>
									<div className="font-weight-5">{rec.enterpriseUpdateName}</div>
									<div>{rec.updatedByName}</div>
									<div>{rec.updatedDate
										? dayjs(rec.updatedDate).format("HH:mm - DD/MM/YYYY")
										: null}</div>
								</div>
							</Flex>}
						<div></div>
					</div>
				),
			},
		],
		[navigate, selectOne, modalMode],
	);

	const actionColumn: TableColumnsType<any> = disableActions
		? []
		: [
			{
				title: "",
				key: "actions",
				width: 80,
				fixed: 'right',
				render: (_: any, rec) => {
					const items: MenuProps["items"] = [];

					if (rec.isCanEdit) {
						items.push({
							key: "1",
							label: t("Chia sẻ", "Chia sẻ"),
							icon: <ShareAltOutlined className="table-view-icon" />,
							onClick: ({ domEvent }) => {
								domEvent.stopPropagation();
								onSharePermission?.(rec);
							},
						});
					}
					if (rec.isCanEdit) {
						items.push(
							{
								key: "2",
								label: t("Chỉnh sửa", "Chỉnh sửa"),
								icon: <EditOutlined className="table-view-icon" />,
								onClick: ({ domEvent }) => {
									domEvent.stopPropagation();
									onEdit?.(rec);
								},
							},
							{
								key: "3",
								label: t("Xoá", "Xoá"),
								icon: <DeleteOutlined className="table-view-icon" />,
								className: "text-danger-del",
								onClick: ({ domEvent }) => {
									domEvent.stopPropagation();
									Modal.confirm({
										title: `${t("Xoá", "Xoá")} ${rec.isUnit ? t("Học liệu", "Học liệu") : "Folder"} ${rec.name}`,
										content: `${rec.isUnit ? t("Học liệu", "Học liệu") : "Folder"} ${t("đã xoá sẽ không thể khôi phục.", "đã xoá sẽ không thể khôi phục.")}`,
										centered: true,
										okText: t("Đồng ý", "Đồng ý"),
										cancelText: t("Huỷ", "Huỷ"),
										onOk: () => onDelete?.(rec.id),
									});
								},
							},
						);
					}
					if (rec.isUnit)
						items.push({
							key: "4",
							label: t("Lịch sử sử dụng", "Lịch sử sử dụng"),
							icon: <HistoryOutlined className="table-view-icon" />,
							onClick: ({ domEvent }) => {
								domEvent.stopPropagation();
								onShowHistory?.(rec.id);
							},
						});

					if (rec.isCanEdit)
						items.push({
							key: "5",
							label: t("Di chuyển", "Di chuyển"),
							icon: <SwapOutlined className="table-view-icon" />,
							onClick: ({ domEvent }) => {
								domEvent.stopPropagation();
								setIsMoveModalOpen(true);
								setMovedId(rec.id);
							},
						});

					return (
						<Space>
							{items?.length === 0 ?
								<Button
									onClick={() => onNavigate(rec)}
								>
									<EyeOutlined style={{ color: "#757575" }} />
								</Button> :
								<Dropdown menu={{ items }} className="action-table">
									<a onClick={(e) => e.preventDefault()}>
										<MoreOutlined />
									</a>
								</Dropdown>}
						</Space>
					);
				},
			},
		];

	const getColumns = useCallback(
		() => [...baseColumns, ...actionColumn],
		[baseColumns, actionColumn],
	);

	useEffect(() => {
		localStorage.setItem('learning-material-view', viewMode);
	}, [viewMode]);

	useEffect(() => {
		setTimeout(() => {
			fetchList();
		}, 200);
	}, [reload, page,
		size,
		tab,
		folderId,
		keyword,
		moduleFilter,
		ownerId,
		updaterId,
		enterpriseUuid,
		updatedDate,]);

	useEffect(() => {
		setSelectedItems([]);
	}, [folderId]);

	useEffect(() => {
		(async () => {
			const [ownRes, schoolRes, editRes] = await Promise.all([
				getOwnerList(),
				getSchoolOwnsList(),
				getLastEditedBy(),
			]);
			setDataOptions({
				ownerList:
					ownRes?.data?.data?.map((i: any) => ({
						value: i.id,
						label: i.email,
					})) || [],
				schoolOwnsList:
					schoolRes?.data?.data?.map((i: any) => ({
						value: i.enterpriseUuid,
						label: i.universityName,
					})) || [],
				lastEditedBy:
					editRes?.data?.data?.map((i: any) => ({
						value: i.id,
						label: i.email,
					})) || [],
			});
		})();
	}, []);

	const renderCards = () => {
		const selectableItems = rows.filter(item => item.isCanEdit);
		const allSelectableSelected = selectableItems.length > 0 &&
			selectableItems.every(item => selectedItems.some(selected => selected.id === item.id));

		return (
			<>
				{allowMultiDelete && viewMode === 'card' && (
					<div className="card-selection-toolbar">
						<Checkbox
							checked={allSelectableSelected}
							indeterminate={selectedItems.length > 0 && selectedItems.length < selectableItems.length}
							onChange={(e) => {
								if (e.target.checked) {
									setSelectedItems(selectableItems);
								} else {
									setSelectedItems([]);
								}
							}}
						>
							{t("Chọn tất cả", "Chọn tất cả")} ({selectedItems.length}/{selectableItems.length})
						</Checkbox>
					</div>
				)}

				<CardData
					data={rows}
					loading={loading}
					totalRecords={total}
					pageNumber={page}
					pageSize={size}
					setPageNumber={onChangePage}
					setPageSize={onChangeSize}
					showFilter={showFilter}
					modalMode={modalMode}
					renderItem={(item: any) => {
						const isSelected = selectOne
							? selectedRow?.some((r) => r.id === item.id)
							: selectedItems.some((i) => i.id === item.id);

						return (
							<ResourceCard
								key={item.id}
								record={item}
								selectable={selectOne}
								selected={isSelected}
								modalMode={modalMode}
								showCheckbox={allowMultiDelete}
								isBeingMoved={modalMode === 'moveItem' && (
									(propMovedId && item.id === propMovedId) ||
									(movingItems && movingItems.some(moving => moving.id === item.id)) ||
									(movedId && item.id === movedId) ||
									selectedItems.some(selected => selected.id === item.id)
								)}
								onSelect={() => {
									if (selectOne) {
										const isCurrentlySelected = selectedRow?.some((r) => r.id === item.id);
										onSelectRow?.(isCurrentlySelected ? [] : [item]);
									} else if (allowMultiDelete) {
										setSelectedItems(prev => {
											const isCurrentlySelected = prev.some(i => i.id === item.id);
											if (isCurrentlySelected) {
												return prev.filter(i => i.id !== item.id);
											} else {
												return [...prev, item];
											}
										});
									}
								}}
								disableActions={disableActions}
								onEdit={onEdit}
								onDelete={(id: number, name: string) =>
									Modal.confirm({
										title: `${t("Xoá", "Xoá")} ${item.isUnit ? t("Học liệu", "Học liệu") : "Folder"} ${name}`,
										content: `${item.isUnit ? t("Học liệu", "Học liệu") : "Folder"} ${t("đã xoá sẽ không thể khôi phục.", "đã xoá sẽ không thể khôi phục.")}`,
										centered: true,
										okText: t("Đồng ý", "Đồng ý"),
										cancelText: t("Huỷ", "Huỷ"),
										onOk: () => onDelete(id),
									})
								}
								onViewHistory={onShowHistory}
								onSharePermission={onSharePermission}
								onMoveItem={(id: number) => {
									setIsMoveModalOpen(true);
									setMovedId(id);
								}}
							/>
						);
					}}
				/>
			</>
		);
	};

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		const handleResize = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				setTableScroll(calculateTableScroll());
			}, 16);
		};

		setTableScroll(calculateTableScroll());
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(timeoutId);
		};
	}, [calculateTableScroll]);

	useEffect(() => {
		setTableScroll(calculateTableScroll());
	}, [showFilter, modalMode, calculateTableScroll]);

	const renderTable = () => {
		let showCheckboxColumn = false;
		let checkboxLogic = undefined;

		if (modalMode === 'saveTemplate' || modalMode === 'moveItem') {
			showCheckboxColumn = false;
		} else if (selectOne) {
			showCheckboxColumn = true;
			checkboxLogic = (rec: any) => !rec.isUnit;
		} else if (allowMultiDelete) {
			showCheckboxColumn = true;
			checkboxLogic = (rec: any) => !rec.isCanEdit;
		}

		return (
			<TableData
				dataColumns={getColumns()}
				dataTable={rows}
				loadingTable={loading}
				pageNumber={page}
				pageSize={size}
				totalRecords={total}
				setPageNumber={onChangePage}
				setPageSize={onChangeSize}
				scroll={tableScroll}
				pagination
				hideSelection={showCheckboxColumn}
				selectOneRow={selectOne}
				setSelectedRowKeysProps={handleSelectRows}
				handleTableChangeProps={() => null}
				rowSelected={selectOne ? selectedRow : selectedItems}
				disableRowCheckbox={checkboxLogic}
			/>
		);
	};

	const tabs: TabsProps["items"] = [
		...(!isSharedFolder && (!disableTabChange || tab === "1")
			? [{
				key: "1",
				label: t("Của tôi", "Của tôi"),
				children: viewMode === "table" ? renderTable() : renderCards(),
			}]
			: []),

		...(!isMineFolder && (!disableTabChange || tab === "2")
			? [{
				key: "2",
				label: t("Được chia sẻ", "Được chia sẻ"),
				children: viewMode === "table" ? renderTable() : renderCards(),
			}]
			: []),
	];

	const handleMoveItem = async (data: any) => {
		setIsSaving(true);
		const payload = {
			parentFolderId: data.folderId,
			folderIds: movedId ? [movedId] : selectedItems.map(item => item.id)
		};

		try {
			await moveItemLearningMaterial(payload);
			message.success(t("Di chuyển folder/học liệu thành công", "Di chuyển folder/học liệu thành công"));

			setIsMoveModalOpen(false);
			navigate(`/${routesConfig.courseLibrary}/${routesConfig.learningRepository}/${data.folderId}${searchParams.toString() ? "?" + searchParams.toString() : ""}`)
			setMovedId(null);
			setSelectedItems([]);
			// fetchList();
		} catch (error) {
		} finally {
			setIsSaving(false);
		}
	};

	const getMovingItemsTab = (): "1" | "2" => {
		if (movedId) {
			const item = rows.find(r => r.id === movedId);
			return item?.isMine ? "1" : "2";
		}
		if (selectedItems.length > 0) {
			return selectedItems[0]?.isMine ? "1" : "2";
		}
		return tab as "1" | "2";
	};

	const handleBulkMove = () => {
		setIsMoveModalOpen(true);
		setMovedId(null);
	};

	return (
		<div className="learning-material-view">
			{showFilter && (
				<FormFilter
					form={form}
					showFilter={showFilter}
					handleCloseFilter={onCloseFilter}
					dataOptions={dataOptions}
					clearFilter={() => {
						form.resetFields();
						set("ownerId", undefined);
						set("enterpriseUuid", undefined);
						set("updaterId", undefined);
						set("updatedDate", ["", ""]);
						set("page", 1);
					}}
				/>
			)}
			<Tabs
				items={tabs}
				onChange={(k) => {
					if (!disableTabChange) {
						setFilter("tab", k as "1" | "2");
						setFilter("page", 1);
					}
				}}
				style={{ width: "100%" }}
				tabBarExtraContent={<div className="flex gap-8">
					{allowMultiDelete && selectedItems.length > 0 && (
						<Button
							onClick={handleBulkMove}
							icon={<SwapOutlined />}
						>
							{t("Di chuyển", "Di chuyển")} {selectedItems.length} {t("mục", "mục")}
						</Button>
					)}

					{allowMultiDelete && selectedItems.length > 0 && (
						<Button
							danger
							loading={isDeleting}
							onClick={onDeleteMultiple}
							icon={<DeleteOutlined />}
						>
							{t("Xoá", "Xoá")} {selectedItems.length} {t("mục", "mục")}
						</Button>
					)}

					<Button
						onClick={() => setViewMode('table')}
						className={`${viewMode === 'table' ? 'button-choose' : ''}`}
					>
						<TableOutlined />
					</Button>

					<Button
						onClick={() => setViewMode('card')}
						className={`${viewMode === 'card' ? 'button-choose' : ''}`}
					>
						<AppstoreOutlined />
					</Button>

					{tabBarExtraContent}
				</div>}
				activeKey={tab}
			/>

			<ChooseLearningMaterialModal
				open={isMoveModalOpen}
				onCancel={() => {
					setIsMoveModalOpen(false);
					setMovedId(null);
				}}
				mode="moveItem"
				onSaveTemplate={(data) => {
					handleMoveItem(data);
				}}
				isSaving={isSaving}
				selectedItems={selectedItems}
				movedId={movedId}
			// fixedTab={getMovingItemsTab()}
			/>
		</div>
	);
};

export default LearningMaterialView;